import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getResumeById, createResume, updateResume, generateAIResume } from '../services/resumeService';
import { useAuth } from '../context/AuthContext';
import { ExperienceTab, EducationTab, SkillsTab, ProjectsTab } from './ResumeFormTabs';
import '../styles/ResumeBuilder.css';

const ResumeBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const STORAGE_KEY = `resume_draft_${id || 'new'}`;

  const [formData, setFormData] = useState({
    title: '',
    targetPosition: '',
    templateId: 'modern',
    personalInfo: {
      firstName: '',
      lastName: '',
      email: user?.email || '',
      phone: '',
      location: { city: '', state: '', country: '' },
      linkedin: '',
      github: '',
      portfolio: '',
      website: ''
    },
    summary: '',
    experience: [],
    education: [],
    skills: {
      technical: [],
      soft: [],
      languages: [],
      other: []
    },
    projects: [],
    certifications: [],
    awards: [],
    volunteer: [],
    settings: {
      sections: {
        summary: true,
        experience: true,
        education: true,
        skills: true,
        projects: true,
        certifications: true,
        awards: true,
        volunteer: false
      },
      colorScheme: {
        primary: '#2563eb',
        secondary: '#64748b',
        text: '#1e293b',
        background: '#ffffff'
      },
      font: 'Inter',
      fontSize: 'medium',
      layout: 'single-column',
      pageMargin: 'standard'
    }
  });

  // Load from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData && !isEditMode) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(prev => ({
          ...prev,
          ...parsed,
          personalInfo: {
            ...prev.personalInfo,
            ...parsed.personalInfo,
            email: user?.email || parsed.personalInfo?.email || ''
          }
        }));
      } catch (error) {
        console.error('Error loading from localStorage:', error);
      }
    }
  }, []);

  // Save to localStorage when formData changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
    }, 500); // Debounce by 500ms

    return () => clearTimeout(timeoutId);
  }, [formData, STORAGE_KEY]);

  useEffect(() => {
    if (isEditMode) {
      fetchResume();
    }
  }, [id]);

  const fetchResume = async () => {
    try {
      setLoading(true);
      const data = await getResumeById(id);
      setFormData(data.resume);
    } catch (error) {
      console.error('Error fetching resume:', error);
      alert('Failed to fetch resume');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    if (section === 'root') {
      setFormData(prev => ({ ...prev, [field]: value }));
    } else {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    }
  };

  const handleArrayAdd = (section, item = {}) => {
    setFormData(prev => ({
      ...prev,
      [section]: [...prev[section], item]
    }));
  };

  const handleArrayRemove = (section, index) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  const handleArrayUpdate = (section, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSkillAdd = (type, skill) => {
    if (!skill.trim()) return;
    setFormData(prev => ({
      ...prev,
      skills: {
        ...prev.skills,
        [type]: [...(prev.skills[type] || []), skill]
      }
    }));
  };

  const handleSkillRemove = (type, index) => {
    setFormData(prev => ({
      ...prev,
      skills: {
        ...prev.skills,
        [type]: prev.skills[type].filter((_, i) => i !== index)
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    const errors = [];

    // Required fields
    if (!formData.title || formData.title.trim().length === 0) {
      errors.push('Resume title is required');
    }
    if (!formData.personalInfo.firstName || formData.personalInfo.firstName.trim().length === 0) {
      errors.push('First name is required');
    }
    if (!formData.personalInfo.lastName || formData.personalInfo.lastName.trim().length === 0) {
      errors.push('Last name is required');
    }
    if (!formData.personalInfo.email || formData.personalInfo.email.trim().length === 0) {
      errors.push('Email is required');
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.personalInfo.email && !emailRegex.test(formData.personalInfo.email)) {
      errors.push('Please enter a valid email address');
    }

    // Phone validation (if provided)
    if (formData.personalInfo.phone && formData.personalInfo.phone.trim().length > 0) {
      const phoneRegex = /^[\d\s\+\-\(\)]+$/;
      if (!phoneRegex.test(formData.personalInfo.phone) || formData.personalInfo.phone.replace(/\D/g, '').length < 10) {
        errors.push('Phone number must contain at least 10 digits');
      }
    }

    // URL validation
    const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    if (formData.personalInfo.linkedin && formData.personalInfo.linkedin.trim().length > 0 && !urlRegex.test(formData.personalInfo.linkedin)) {
      errors.push('Please enter a valid LinkedIn URL');
    }
    if (formData.personalInfo.github && formData.personalInfo.github.trim().length > 0 && !urlRegex.test(formData.personalInfo.github)) {
      errors.push('Please enter a valid GitHub URL');
    }
    if (formData.personalInfo.portfolio && formData.personalInfo.portfolio.trim().length > 0 && !urlRegex.test(formData.personalInfo.portfolio)) {
      errors.push('Please enter a valid Portfolio URL');
    }
    if (formData.personalInfo.website && formData.personalInfo.website.trim().length > 0 && !urlRegex.test(formData.personalInfo.website)) {
      errors.push('Please enter a valid Website URL');
    }

    // Summary validation
    if (formData.summary && formData.summary.length > 1000) {
      errors.push('Summary must be less than 1000 characters');
    }

    // Experience validation
    formData.experience.forEach((exp, index) => {
      if (!exp.jobTitle || exp.jobTitle.trim().length === 0) {
        errors.push(`Experience #${index + 1}: Job title is required`);
      }
      if (!exp.company || exp.company.trim().length === 0) {
        errors.push(`Experience #${index + 1}: Company name is required`);
      }
      if (!exp.startDate) {
        errors.push(`Experience #${index + 1}: Start date is required`);
      }
      if (exp.startDate && exp.endDate && new Date(exp.startDate) > new Date(exp.endDate)) {
        errors.push(`Experience #${index + 1}: Start date cannot be after end date`);
      }
    });

    // Education validation
    formData.education.forEach((edu, index) => {
      if (!edu.degree || edu.degree.trim().length === 0) {
        errors.push(`Education #${index + 1}: Degree is required`);
      }
      if (!edu.institution || edu.institution.trim().length === 0) {
        errors.push(`Education #${index + 1}: Institution name is required`);
      }
      if (edu.startDate && edu.endDate && new Date(edu.startDate) > new Date(edu.endDate)) {
        errors.push(`Education #${index + 1}: Start date cannot be after end date`);
      }
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      setActiveTab('basic'); // Switch to basic tab to show errors
      alert('Please fix the following errors:\n\n' + errors.join('\n'));
      return;
    }

    setValidationErrors([]);

    try {
      setSaving(true);
      if (isEditMode) {
        await updateResume(id, formData);
        alert('Resume updated successfully');
        // Clear localStorage after successful update
        localStorage.removeItem(STORAGE_KEY);
      } else {
        const result = await createResume(formData);
        alert('Resume created successfully');
        // Clear localStorage after successful creation
        localStorage.removeItem(STORAGE_KEY);
        navigate(`/resumes/edit/${result.resume._id}`);
      }
    } catch (error) {
      console.error('Error saving resume:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      
      if (error.response?.data?.errors) {
        setValidationErrors(error.response.data.errors);
        alert('Validation failed:\n\n' + error.response.data.errors.join('\n'));
      } else if (error.response?.data?.message) {
        alert('Error: ' + error.response.data.message);
      } else if (error.message) {
        alert('Error: ' + error.message);
      } else {
        alert('Failed to save resume. Please check the console for details.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAIPrefill = async () => {
    // Show a prompt to get basic info
    const name = prompt('Enter your full name:', formData.personalInfo.firstName + ' ' + formData.personalInfo.lastName || 'John Doe');
    if (!name) return;

    const position = prompt('Enter target position:', formData.targetPosition || 'Software Engineer');
    if (!position) return;

    const yearsOfExperience = prompt('Enter years of experience:', '3');
    if (!yearsOfExperience) return;

    const industry = prompt('Enter industry:', 'Technology');
    if (!industry) return;

    try {
      setAiGenerating(true);
      const response = await generateAIResume({
        name,
        position,
        yearsOfExperience,
        industry
      });

      if (response.success && response.data) {
        const [firstName, ...lastNameParts] = name.split(' ');
        const lastName = lastNameParts.join(' ') || '';

        // Merge AI data with existing form data
        setFormData(prev => ({
          ...prev,
          title: response.data.title || prev.title,
          targetPosition: response.data.targetPosition || position,
          summary: response.data.summary || prev.summary,
          experience: response.data.experience || prev.experience,
          education: response.data.education || prev.education,
          skills: {
            technical: response.data.skills?.technical || prev.skills.technical,
            soft: response.data.skills?.soft || prev.skills.soft,
            languages: response.data.skills?.languages || prev.skills.languages,
            other: response.data.skills?.other || prev.skills.other
          },
          personalInfo: {
            ...prev.personalInfo,
            firstName: firstName || prev.personalInfo.firstName,
            lastName: lastName || prev.personalInfo.lastName
          }
        }));

        alert('AI data generated successfully! Review and modify as needed.');
      }
    } catch (error) {
      console.error('Error generating AI data:', error);
      alert('Failed to generate AI data. Please try again.');
    } finally {
      setAiGenerating(false);
    }
  };

  const templates = [
    { id: 'modern', name: 'Modern', color: '#2563eb' },
    { id: 'professional', name: 'Professional', color: '#059669' },
    { id: 'creative', name: 'Creative', color: '#dc2626' },
    { id: 'minimal', name: 'Minimal', color: '#64748b' },
    { id: 'executive', name: 'Executive', color: '#7c3aed' },
    { id: 'technical', name: 'Technical', color: '#0891b2' },
    { id: 'academic', name: 'Academic', color: '#ea580c' },
    { id: 'classic', name: 'Classic', color: '#4b5563' },
    { id: 'bold', name: 'Bold', color: '#be123c' },
    { id: 'elegant', name: 'Elegant', color: '#9333ea' }
  ];

  if (loading) {
    return <div className="resume-builder-loading">Loading...</div>;
  }

  return (
    <div className="resume-builder-container">
      <div className="card">
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button className="btn-back" onClick={() => navigate('/resumes')}>
              ← Back
            </button>
            <div>
              <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>
                {isEditMode ? '✏️ Edit Resume' : '📝 Create New Resume'}
              </h2>
              <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
                {isEditMode ? 'Update your resume information' : 'Build your professional resume'}
              </p>
            </div>
          </div>
          <div className="header-actions">
            <button 
              type="button"
              className="btn-ai"
              onClick={handleAIPrefill}
              disabled={aiGenerating}
            >
              {aiGenerating ? '✨ Generating...' : '✨ AI Prefill'}
            </button>
            <button 
              className="btn-preview"
              onClick={() => navigate(`/resumes/preview/${id}`)}
              disabled={!isEditMode}
            >
              👁️ Preview
            </button>
            <button 
              className="btn-save"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Saving...' : '💾 Save'}
            </button>
          </div>
        </div>

      {validationErrors.length > 0 && (
        <div style={{
          background: '#fed7d7',
          border: '1px solid #f56565',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <h4 style={{ color: '#c53030', margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600 }}>
            ⚠️ Please fix the following errors:
          </h4>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#742a2a' }}>
            {validationErrors.map((error, index) => (
              <li key={index} style={{ fontSize: '13px', marginBottom: '4px' }}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="builder-tabs">
        <button 
          className={`tab ${activeTab === 'basic' ? 'active' : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          📝 Basic Info
        </button>
        <button 
          className={`tab ${activeTab === 'experience' ? 'active' : ''}`}
          onClick={() => setActiveTab('experience')}
        >
          💼 Experience
        </button>
        <button 
          className={`tab ${activeTab === 'education' ? 'active' : ''}`}
          onClick={() => setActiveTab('education')}
        >
          🎓 Education
        </button>
        <button 
          className={`tab ${activeTab === 'skills' ? 'active' : ''}`}
          onClick={() => setActiveTab('skills')}
        >
          ⚡ Skills
        </button>
        <button 
          className={`tab ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          🚀 Projects
        </button>
        <button 
          className={`tab ${activeTab === 'design' ? 'active' : ''}`}
          onClick={() => setActiveTab('design')}
        >
          🎨 Design
        </button>
      </div>

      <form className="builder-form" onSubmit={handleSubmit}>
        {/* BASIC INFO TAB */}
        {activeTab === 'basic' && (
          <div className="form-section">
            <h2>Resume Information</h2>
            <div className="form-row">
              <div className="form-group full">
                <label>Resume Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('root', 'title', e.target.value)}
                  placeholder="e.g., Software Engineer - Google"
                  required
                />
              </div>
              <div className="form-group full">
                <label>Target Position</label>
                <input
                  type="text"
                  value={formData.targetPosition}
                  onChange={(e) => handleInputChange('root', 'targetPosition', e.target.value)}
                  placeholder="e.g., Senior Frontend Developer"
                />
              </div>
            </div>

            <h2>Personal Information</h2>
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.personalInfo.firstName}
                  onChange={(e) => handleInputChange('personalInfo', 'firstName', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.personalInfo.lastName}
                  onChange={(e) => handleInputChange('personalInfo', 'lastName', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.personalInfo.email}
                  onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
                  disabled
                  required
                  style={{ backgroundColor: '#f8fafc', cursor: 'not-allowed' }}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.personalInfo.phone}
                  onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.personalInfo.location?.city || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    personalInfo: {
                      ...prev.personalInfo,
                      location: { ...prev.personalInfo.location, city: e.target.value }
                    }
                  }))}
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  value={formData.personalInfo.location?.state || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    personalInfo: {
                      ...prev.personalInfo,
                      location: { ...prev.personalInfo.location, state: e.target.value }
                    }
                  }))}
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  value={formData.personalInfo.location?.country || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    personalInfo: {
                      ...prev.personalInfo,
                      location: { ...prev.personalInfo.location, country: e.target.value }
                    }
                  }))}
                />
              </div>
            </div>

            <h3>Social Links</h3>
            <div className="form-row">
              <div className="form-group">
                <label>LinkedIn</label>
                <input
                  type="url"
                  value={formData.personalInfo.linkedin || ''}
                  onChange={(e) => handleInputChange('personalInfo', 'linkedin', e.target.value)}
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
              <div className="form-group">
                <label>GitHub</label>
                <input
                  type="url"
                  value={formData.personalInfo.github || ''}
                  onChange={(e) => handleInputChange('personalInfo', 'github', e.target.value)}
                  placeholder="https://github.com/username"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Portfolio</label>
                <input
                  type="url"
                  value={formData.personalInfo.portfolio || ''}
                  onChange={(e) => handleInputChange('personalInfo', 'portfolio', e.target.value)}
                  placeholder="https://yourportfolio.com"
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={formData.personalInfo.website || ''}
                  onChange={(e) => handleInputChange('personalInfo', 'website', e.target.value)}
                  placeholder="https://yourwebsite.com"
                />
              </div>
            </div>

            <h3>Professional Summary</h3>
            <div className="form-group full">
              <textarea
                value={formData.summary}
                onChange={(e) => handleInputChange('root', 'summary', e.target.value)}
                placeholder="Write a brief summary highlighting your key skills and experience..."
                rows={5}
                maxLength={1000}
              />
              <small>{formData.summary?.length || 0} / 1000 characters</small>
            </div>
          </div>
        )}

        {/* EXPERIENCE TAB */}
        {activeTab === 'experience' && (
          <ExperienceTab 
            experience={formData.experience}
            setExperience={(exp) => setFormData(prev => ({ ...prev, experience: exp }))}
          />
        )}

        {/* EDUCATION TAB */}
        {activeTab === 'education' && (
          <EducationTab 
            education={formData.education}
            setEducation={(edu) => setFormData(prev => ({ ...prev, education: edu }))}
          />
        )}

        {/* SKILLS TAB */}
        {activeTab === 'skills' && (
          <SkillsTab 
            skills={formData.skills}
            setSkills={(skills) => setFormData(prev => ({ ...prev, skills }))}
          />
        )}

        {/* PROJECTS TAB */}
        {activeTab === 'projects' && (
          <ProjectsTab 
            projects={formData.projects}
            setProjects={(projects) => setFormData(prev => ({ ...prev, projects }))}
          />
        )}

        {/* DESIGN TAB */}
        {activeTab === 'design' && (
          <div className="form-section">
            <h2>Design Settings</h2>
            
            <div className="form-group full">
              <label>Template</label>
              <select
                value={formData.templateId}
                onChange={(e) => handleInputChange('root', 'templateId', e.target.value)}
              >
                <option value="modern">Modern</option>
                <option value="professional">Professional</option>
                <option value="creative">Creative</option>
                <option value="minimal">Minimal</option>
                <option value="executive">Executive</option>
                <option value="technical">Technical</option>
                <option value="academic">Academic</option>
                <option value="classic">Classic</option>
                <option value="bold">Bold</option>
                <option value="elegant">Elegant</option>
              </select>
            </div>

            <h3>Color Scheme</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Primary Color</label>
                <input
                  type="color"
                  value={formData.settings?.colorScheme?.primary || '#2563eb'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      colorScheme: { ...prev.settings.colorScheme, primary: e.target.value }
                    }
                  }))}
                />
              </div>
              <div className="form-group">
                <label>Secondary Color</label>
                <input
                  type="color"
                  value={formData.settings?.colorScheme?.secondary || '#64748b'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      colorScheme: { ...prev.settings.colorScheme, secondary: e.target.value }
                    }
                  }))}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Font</label>
                <select
                  value={formData.settings?.font || 'Inter'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, font: e.target.value }
                  }))}
                >
                  <option value="Inter">Inter</option>
                  <option value="Roboto">Roboto</option>
                  <option value="Open Sans">Open Sans</option>
                  <option value="Lato">Lato</option>
                  <option value="Montserrat">Montserrat</option>
                  <option value="Poppins">Poppins</option>
                  <option value="Source Sans Pro">Source Sans Pro</option>
                  <option value="Raleway">Raleway</option>
                </select>
              </div>
              <div className="form-group">
                <label>Font Size</label>
                <select
                  value={formData.settings?.fontSize || 'medium'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, fontSize: e.target.value }
                  }))}
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </form>
      </div>
    </div>
  );
};

export default ResumeBuilder;
